/**
 * P-2 会議詳細 (`/board-meetings/:id`)。本機能の主画面。設計: docs/design/v05-board-meeting-pdca-detail.md §7-2。
 *
 * ⚠️ ノード名解決は `GET /board-meetings/creatable-nodes` (会議スコープの書込み可能ノード) を使う。
 * 論点 (finding) の `node_id` はブリーフ対象スコープ内の任意店舗であり、必ずしもこの一覧に
 * 含まれるとは限らないため、見つからない場合は node_id をそのまま表示する (簡略化、実装時の判断)。
 */
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Clock, RefreshCw, Sparkles } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { useAuthStore, hasTenantWidePermission } from '@/stores/auth';
import { Button } from '@/components/Button';
import { Modal, ConfirmDialog } from '@/components/Modal';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BoardEmptyState } from '@/components/board/BoardEmptyState';
import { BoardChatPanel } from '@/components/board/BoardChatPanel';
import { InitiativeOwnersEditor, type InitiativeOwner } from '@/components/board/InitiativeOwnersEditor';
import { MonthPicker } from '@/components/MonthPicker';
import type { BoardNode } from '@/lib/board-node';

type BoardMeeting = {
  meeting_id: string; title: string; meeting_date: string; target_month: string | null;
  scope_node_id: string; attendees_note: string | null; status: 'planned' | 'closed';
  created_at: string; updated_at: string; row_version: number;
};
type BriefStatus = 'pending' | 'generating' | 'succeeded' | 'failed';
type BoardBrief = {
  brief_id: string; meeting_id: string | null; target_month: string; scope_node_id: string;
  status: BriefStatus; summary_body: string; discarded_findings: number;
  generation_started_at: string | null; generated_at: string | null; error_message: string | null; created_at: string;
};
type BoardFinding = {
  finding_id: string; node_id: string; seq: number;
  category: 'sales' | 'cost' | 'labor' | 'opex' | 'data_quality' | 'action_followup';
  title: string; narrative: string; impact_fact_key: string | null; impact_amount: number | null;
  evidence: string[] | null; decision_required: boolean; confidence: string;
  disposition: 'open' | 'promoted' | 'dismissed' | 'carried_over'; dismissed_reason: string | null; created_at: string;
};
type BoardInitiative = {
  initiative_id: string; node_id: string; title: string; status: string;
  origin_meeting_id: string | null; origin_finding_id: string | null;
};
const CATEGORY_LABEL: Record<string, string> = {
  sales: '売上', cost: '原価', labor: '人件費', opex: '諸経費', data_quality: 'データ品質', action_followup: 'フォローアップ',
};
const KPI_TYPE_LABEL: Record<string, string> = {
  cost_rate: '原価率', gross_margin: '粗利率', labor_rate: '人件費率', opex_rate: '諸経費率',
  sales: '売上', customer_count: '来客数', avg_price: '客単価',
};

const jpy = (n: number | null) => (n == null ? '—' : `${n < 0 ? '−' : ''}${Math.abs(n).toLocaleString('ja-JP')}円`);

type PromoteDraft = {
  owners: InitiativeOwner[]; dueDate: string; targetKpiType: string;
  baselineMonth: string; baselineValue: string; targetMonth: string; targetValue: string;
};
const emptyPromoteDraft: PromoteDraft = {
  owners: [], dueDate: '', targetKpiType: '', baselineMonth: '', baselineValue: '', targetMonth: '', targetValue: '',
};

export function BoardMeetingDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canManage = hasTenantWidePermission(user?.effective_permissions, 'board_meeting', 'manage');

  const [promoteTarget, setPromoteTarget] = useState<BoardFinding | null>(null);
  const [promoteDraft, setPromoteDraft] = useState<PromoteDraft>(emptyPromoteDraft);
  const [dismissTarget, setDismissTarget] = useState<BoardFinding | null>(null);
  const [dismissReason, setDismissReason] = useState('');
  const [regenerateConfirm, setRegenerateConfirm] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const { data: meeting, isLoading: meetingLoading, isError: meetingError } = useQuery<BoardMeeting>({
    queryKey: ['board-meeting', id],
    queryFn: () => apiFetch(`/board-meetings/${id}`),
    enabled: !!id,
  });

  const { data: nodesData } = useQuery<{ nodes: BoardNode[] }>({
    queryKey: ['board-meetings', 'creatable-nodes'],
    queryFn: () => apiFetch('/board-meetings/creatable-nodes'),
  });
  const nodeById = useMemo(() => new Map((nodesData?.nodes ?? []).map((n) => [n.node_id, n])), [nodesData]);
  const nodeName = (nodeId: string) => nodeById.get(nodeId)?.name ?? nodeId;

  const { data: briefData, isLoading: briefLoading } = useQuery<{ brief: BoardBrief | null; findings: BoardFinding[] }>({
    queryKey: ['board-meeting-brief', id],
    queryFn: () => apiFetch(`/board-meetings/${id}/brief`),
    enabled: !!id,
    // §7-2-a: pending/generating の間はポーリングする (§8-37対策)
    refetchInterval: (query) => {
      const status = query.state.data?.brief?.status;
      return status === 'pending' || status === 'generating' ? 3000 : false;
    },
  });
  const brief = briefData?.brief ?? null;
  const findings = briefData?.findings ?? [];
  const followupFindings = findings.filter((f) => f.category === 'action_followup');
  const regularFindings = findings.filter((f) => f.category !== 'action_followup')
    .sort((a, b) => (b.impact_amount ?? -Infinity) - (a.impact_amount ?? -Infinity));

  const { data: initiativesData } = useQuery<{ initiatives: BoardInitiative[] }>({
    queryKey: ['board-initiatives'],
    queryFn: () => apiFetch('/board-initiatives'),
  });
  const meetingInitiatives = (initiativesData?.initiatives ?? []).filter((i) => i.origin_meeting_id === id);

  const generateMut = useMutation({
    mutationFn: (force: boolean) => apiFetch(`/board-meetings/${id}/brief${force ? '?force=true' : ''}`, { method: 'POST' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['board-meeting-brief', id] }); toast.success('ブリーフの生成を開始しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : 'ブリーフの生成に失敗しました'),
  });

  const promoteMut = useMutation({
    mutationFn: (d: PromoteDraft) => apiFetch<{ initiative_id: string }>(`/board-brief-findings/${promoteTarget!.finding_id}/promote`, {
      method: 'POST',
      body: JSON.stringify({
        owners: d.owners,
        due_date: d.dueDate || null,
        target_kpi_type: d.targetKpiType || null,
        baseline_month: d.baselineMonth ? `${d.baselineMonth}-01` : null,
        baseline_value: d.baselineValue ? Number(d.baselineValue) : null,
        target_month: d.targetMonth ? `${d.targetMonth}-01` : null,
        target_value: d.targetValue ? Number(d.targetValue) : null,
      }),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['board-meeting-brief', id] });
      qc.invalidateQueries({ queryKey: ['board-initiatives'] });
      setPromoteTarget(null); setPromoteDraft(emptyPromoteDraft);
      toast.success('施策化しました');
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '施策化に失敗しました'),
  });

  const dismissMut = useMutation({
    mutationFn: () => apiFetch(`/board-brief-findings/${dismissTarget!.finding_id}/dismiss`, {
      method: 'POST', body: JSON.stringify({ reason: dismissReason }),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['board-meeting-brief', id] });
      setDismissTarget(null); setDismissReason('');
      toast.success('却下しました');
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '却下に失敗しました'),
  });

  const deleteMut = useMutation({
    mutationFn: () => apiFetch(`/board-meetings/${id}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('会議を削除しました'); navigate('/board-meetings'); },
    onError: (e) => { toast.error(e instanceof ApiClientError ? e.message : '削除に失敗しました'); setDeleteConfirm(false); },
  });

  const editMut = useMutation({
    mutationFn: (patch: { title: string; meeting_date: string; target_month: string | null; attendees_note: string | null; status: string }) =>
      apiFetch(`/board-meetings/${id}`, { method: 'PATCH', body: JSON.stringify({ ...patch, row_version: meeting!.row_version }) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['board-meeting', id] }); setEditOpen(false); toast.success('更新しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '更新に失敗しました'),
  });

  if (meetingLoading) return <p className="text-sm text-text-sub">読み込んでいます…</p>;
  if (meetingError || !meeting) return <p role="alert" className="text-sm text-alert-text">会議が見つからないか、閲覧する権限がありません。</p>;

  const elapsedSec = brief?.generation_started_at
    ? Math.max(0, Math.floor((Date.now() - new Date(brief.generation_started_at).getTime()) / 1000))
    : 0;

  // ⚠️ 2026-09-15 本間さんからの報告で追加: ブリーフ生成中(pending/generating)に会議の編集・削除が
  // できてしまうと、生成中の非同期処理(runBriefGeneration)が参照しているscope_node_id/target_month
  // が編集で変わったり、削除で会議自体が消えたりして予期せぬ挙動になりうる。生成完了までロックする。
  const briefGenerating = brief?.status === 'pending' || brief?.status === 'generating';

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-page-title text-text-main break-words">{meeting.title}</h1>
          <div className="flex items-center gap-2 flex-wrap mt-1 text-sm text-text-sub">
            <span className="tabular-nums">{meeting.meeting_date}</span>
            <span>{meeting.target_month ? `対象月: ${meeting.target_month.slice(0, 7)}` : '臨時会議 (対象月未設定)'}</span>
            <span>{nodeName(meeting.scope_node_id)}</span>
            <span className={`text-[11px] px-1.5 py-0.5 rounded border whitespace-nowrap ${
              meeting.status === 'closed' ? 'bg-surface-sunken text-text-sub border-surface-border' : 'bg-success-bg text-success-text border-success-border'
            }`}>{meeting.status === 'closed' ? '終了' : '予定'}</span>
          </div>
          {meeting.attendees_note && <p className="text-sm text-text-sub mt-1 whitespace-pre-wrap break-words">{meeting.attendees_note}</p>}
        </div>
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          <Button variant="secondary" size="sm" disabled={briefGenerating} title={briefGenerating ? 'ブリーフ生成中は編集できません' : undefined}
            aria-describedby={briefGenerating ? 'bm-editing-locked' : undefined} onClick={() => setEditOpen(true)}>会議を編集</Button>
          {canManage && (
            <Button variant="ghost" size="sm" disabled={briefGenerating} title={briefGenerating ? 'ブリーフ生成中は削除できません' : undefined}
              aria-describedby={briefGenerating ? 'bm-editing-locked' : undefined} onClick={() => setDeleteConfirm(true)}>削除</Button>
          )}
        </div>
      </div>
      {briefGenerating && (
        <p id="bm-editing-locked" className="text-xs text-text-sub">
          ブリーフを生成している間は、会議の編集・削除はできません。生成が終わるまでお待ちください。
        </p>
      )}

      {/* ブリーフセクション (§7-2-a) */}
      <section className="border border-surface-border rounded-lg bg-white p-4 space-y-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <h2 className="text-sm font-semibold text-text-body">ブリーフ</h2>
          {!meeting.target_month ? (
            <Button variant="primary" size="sm" disabled leftIcon={<Sparkles size={14} />} aria-describedby="bm-no-month">
              ブリーフを生成
            </Button>
          ) : brief?.status === 'succeeded' ? (
            <Button variant="secondary" size="sm" leftIcon={<RefreshCw size={14} />} onClick={() => setRegenerateConfirm(true)}>再生成</Button>
          ) : brief?.status === 'pending' || brief?.status === 'generating' ? (
            <Button variant="primary" size="sm" disabled loading>生成中…</Button>
          ) : (
            <Button variant="primary" size="sm" leftIcon={<Sparkles size={14} />} loading={generateMut.isPending}
              onClick={() => generateMut.mutate(false)}>
              {brief?.status === 'failed' ? '再生成' : 'ブリーフを生成'}
            </Button>
          )}
        </div>
        {!meeting.target_month && (
          <p id="bm-no-month" className="text-xs text-text-sub">対象月を設定するとブリーフを生成できます。<button type="button" className="underline" onClick={() => setEditOpen(true)}>会議を編集</button></p>
        )}

        {briefLoading ? (
          <p className="text-sm text-text-sub">読み込んでいます…</p>
        ) : !meeting.target_month ? (
          <BoardEmptyState title="臨時会議のためブリーフは生成できません" description="会議と施策の管理のみ利用できます。" />
        ) : !brief || brief.status === 'pending' ? (
          brief?.status === 'pending' ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-text-sub text-sm" aria-live="polite">
                <Clock size={16} className="animate-pulse" aria-hidden />
                生成待機中… (経過 {elapsedSec} 秒、通常30〜60秒かかります{elapsedSec > 60 ? ' — 想定より時間がかかっています' : ''})
              </div>
              {/* ⚠️ 2026-09-15追加: サーバー側は5分経過したgenerating/pendingを再クレーム可能だが、
                  それを引き起こす導線がUIに無く、失敗時にstatusが更新されないまま永久に「生成中」
                  表示が残る事故があった(本間さんからの報告)。念のための保険として、5分経過後に
                  手動で再試行できるボタンを出す(通常はAI呼出のtimeoutMs設定で30〜60秒以内に
                  succeeded/failedへ遷移するはずで、これは異常系のみ到達する想定) */}
              {elapsedSec > 300 && (
                <Button variant="secondary" size="sm" leftIcon={<RefreshCw size={14} />} loading={generateMut.isPending}
                  onClick={() => generateMut.mutate(false)}>強制的に再試行</Button>
              )}
            </div>
          ) : (
            <BoardEmptyState title="まだブリーフがありません" />
          )
        ) : brief.status === 'generating' ? (
          <div className="space-y-2" aria-live="polite">
            <div className="h-4 w-2/3 rounded bg-surface-sunken animate-pulse" />
            <div className="h-4 w-1/2 rounded bg-surface-sunken animate-pulse" />
            <div className="flex items-center gap-2 text-text-sub text-sm">
              <Clock size={16} className="animate-pulse" aria-hidden />
              生成中… (経過 {elapsedSec} 秒、通常30〜60秒かかります{elapsedSec > 60 ? ' — 想定より時間がかかっています' : ''})
            </div>
            {elapsedSec > 300 && (
              <Button variant="secondary" size="sm" leftIcon={<RefreshCw size={14} />} loading={generateMut.isPending}
                onClick={() => generateMut.mutate(false)}>強制的に再試行</Button>
            )}
          </div>
        ) : brief.status === 'failed' ? (
          <div className="rounded bg-alert-bg text-alert-text text-sm p-3 flex items-start gap-2">
            <AlertTriangle size={16} className="shrink-0 mt-0.5" aria-hidden />
            <span className="break-words">{brief.error_message ?? '生成に失敗しました。'}</span>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-text-body whitespace-pre-wrap break-words">{brief.summary_body}</p>
            {brief.discarded_findings > 0 && (
              <p className="text-xs text-text-sub">根拠が確認できなかった論点を {brief.discarded_findings} 件除外しました。</p>
            )}

            {followupFindings.length > 0 && (
              <details className="border border-surface-border rounded-lg">
                <summary className="px-3 py-2 text-sm font-semibold text-text-body cursor-pointer min-h-[44px] flex items-center">
                  前回施策のフォローアップ ({followupFindings.length}件、金額は期待値)
                </summary>
                <ul className="divide-y divide-surface-line">
                  {followupFindings.map((f) => (
                    <FindingCard key={f.finding_id} finding={f} nodeName={nodeName} amountLabel="期待値"
                      onPromote={() => { setPromoteTarget(f); setPromoteDraft(emptyPromoteDraft); }}
                      onDismiss={() => setDismissTarget(f)} />
                  ))}
                </ul>
              </details>
            )}

            <div>
              <h3 className="text-sm font-semibold text-text-body mb-2">論点 ({regularFindings.length}件、金額は実測差異)</h3>
              {regularFindings.length === 0 ? (
                <BoardEmptyState title="論点は検出されませんでした" />
              ) : (
                <ul className="space-y-2">
                  {regularFindings.map((f) => (
                    <FindingCard key={f.finding_id} finding={f} nodeName={nodeName} amountLabel="実測差異"
                      onPromote={() => { setPromoteTarget(f); setPromoteDraft(emptyPromoteDraft); }}
                      onDismiss={() => setDismissTarget(f)} />
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </section>

      {/* v0.5 経営会議PDCA 増分「ブリーフ対話AI」P-9: ブリーフ <section> の直後に独立した <section> として
          配置する (ブリーフの多分岐三項の内部に置くと生成中・失敗時・臨時会議で対話が描画されない、
          docs/design/v05-board-meeting-chat-detail.md §7 P-9)。 */}
      <BoardChatPanel
        meetingId={id!}
        topFindings={regularFindings.slice(0, 3).map((f) => ({ finding_id: f.finding_id, title: f.title }))}
        canManage={canManage}
      />

      {/* この会議の施策 */}
      <section className="space-y-2">
        <h2 className="text-sm font-semibold text-text-body">この会議の施策</h2>
        {meetingInitiatives.length === 0 ? (
          <BoardEmptyState title="この会議から起票された施策はまだありません" />
        ) : (
          <ul className="space-y-2">
            {meetingInitiatives.map((i) => (
              <li key={i.initiative_id}>
                <button type="button" onClick={() => navigate(`/board-initiatives/${i.initiative_id}`)}
                  className="w-full text-left border border-surface-border rounded-lg bg-white p-3 min-h-[44px] hover:bg-surface-sunken">
                  <span className="text-sm text-text-body break-words">{i.title}</span>
                  <span className="block text-xs text-text-sub mt-0.5">{nodeName(i.node_id)}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* 施策にするモーダル (AC-3) */}
      <Modal open={!!promoteTarget} title="施策にする" onClose={() => setPromoteTarget(null)} size="md"
        footer={promoteTarget && (
          <>
            <Button variant="secondary" onClick={() => setPromoteTarget(null)}>キャンセル</Button>
            <Button variant="primary" loading={promoteMut.isPending} onClick={() => promoteMut.mutate(promoteDraft)}>施策化する</Button>
          </>
        )}>
        {promoteTarget && (
          <div className="space-y-3">
            <div>
              <p className="text-xs text-text-sub">タイトル (論点から引き継ぎ)</p>
              <p className="text-sm text-text-body break-words">{promoteTarget.title}</p>
            </div>
            <div>
              <label className="block text-xs text-text-sub mb-1">担当者 (任意、複数可)</label>
              <InitiativeOwnersEditor value={promoteDraft.owners}
                onChange={(owners) => setPromoteDraft({ ...promoteDraft, owners })} enabled={!!promoteTarget} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-text-sub mb-1" htmlFor="pf-due">期限 (任意)</label>
                <input id="pf-due" type="date" value={promoteDraft.dueDate}
                  onChange={(e) => setPromoteDraft({ ...promoteDraft, dueDate: e.target.value })}
                  className="w-full min-h-[44px] px-3 rounded border border-surface-border text-sm" />
              </div>
              <div>
                <label className="block text-xs text-text-sub mb-1" htmlFor="pf-kpi">測定するKPI (任意)</label>
                <Select value={promoteDraft.targetKpiType || '__none__'} onValueChange={(v) => setPromoteDraft({ ...promoteDraft, targetKpiType: v === '__none__' ? '' : v })}>
                  <SelectTrigger id="pf-kpi" aria-label="測定するKPI"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">未設定</SelectItem>
                    {Object.entries(KPI_TYPE_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {promoteDraft.targetKpiType && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-text-sub mb-1" htmlFor="pf-bm">基準月・値</label>
                  <div className="flex gap-1.5">
                    <MonthPicker id="pf-bm" aria-label="基準月" value={promoteDraft.baselineMonth}
                      onChange={(v) => setPromoteDraft({ ...promoteDraft, baselineMonth: v })}
                      className="min-h-[44px] px-2 rounded border border-surface-border text-sm flex-1 min-w-0" />
                    <input type="number" step="any" aria-label="基準値" value={promoteDraft.baselineValue}
                      onChange={(e) => setPromoteDraft({ ...promoteDraft, baselineValue: e.target.value })}
                      className="min-h-[44px] px-2 rounded border border-surface-border text-sm w-24 appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-text-sub mb-1" htmlFor="pf-tm">目標月・値</label>
                  <div className="flex gap-1.5">
                    <MonthPicker id="pf-tm" aria-label="目標月" value={promoteDraft.targetMonth}
                      onChange={(v) => setPromoteDraft({ ...promoteDraft, targetMonth: v })}
                      className="min-h-[44px] px-2 rounded border border-surface-border text-sm flex-1 min-w-0" />
                    <input type="number" step="any" aria-label="目標値" value={promoteDraft.targetValue}
                      onChange={(e) => setPromoteDraft({ ...promoteDraft, targetValue: e.target.value })}
                      className="min-h-[44px] px-2 rounded border border-surface-border text-sm w-24 appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none" />
                  </div>
                </div>
              </div>
            )}
            <p className="text-xs text-text-sub">期待効果: {jpy(promoteTarget.impact_amount)} (論点の金額から自動転記)</p>
          </div>
        )}
      </Modal>

      {/* 却下モーダル */}
      <Modal open={!!dismissTarget} title="論点を却下しますか" onClose={() => { setDismissTarget(null); setDismissReason(''); }} size="sm"
        footer={dismissTarget && (
          <>
            <Button variant="secondary" onClick={() => { setDismissTarget(null); setDismissReason(''); }}>キャンセル</Button>
            <Button variant="danger" disabled={dismissReason.trim() === ''} loading={dismissMut.isPending} onClick={() => dismissMut.mutate()}>却下する</Button>
          </>
        )}>
        <div className="space-y-2">
          <p className="text-sm text-text-body break-words">{dismissTarget?.title}</p>
          <label className="block text-xs text-text-sub mb-1" htmlFor="dismiss-reason">却下理由 (必須)</label>
          <textarea id="dismiss-reason" value={dismissReason} maxLength={1000} rows={3}
            onChange={(e) => setDismissReason(e.target.value)}
            className="w-full px-3 py-2 rounded border border-surface-border text-sm" />
        </div>
      </Modal>

      <ConfirmDialog
        open={regenerateConfirm} title="ブリーフを再生成しますか" onClose={() => setRegenerateConfirm(false)}
        onConfirm={() => { setRegenerateConfirm(false); generateMut.mutate(true); }}
        confirmLabel="再生成する" loading={generateMut.isPending}
        message="既存のブリーフを置き換えて再生成します。施策化済みの論点は残ります。"
      />

      <ConfirmDialog
        open={deleteConfirm} title="会議を削除しますか" onClose={() => setDeleteConfirm(false)}
        onConfirm={() => deleteMut.mutate()} confirmLabel="削除する" loading={deleteMut.isPending} destructive
        message="この会議とブリーフ・論点を削除します。この操作は取り消せません。施策は残ります。"
      />

      <EditMeetingModal open={editOpen} meeting={meeting} onClose={() => setEditOpen(false)}
        onSave={(patch) => editMut.mutate(patch)} saving={editMut.isPending} />
    </div>
  );
}

function FindingCard({ finding, nodeName, amountLabel, onPromote, onDismiss }: {
  finding: BoardFinding; nodeName: (id: string) => string; amountLabel: string;
  onPromote: () => void; onDismiss: () => void;
}) {
  return (
    <li className="p-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="text-[11px] px-1.5 py-0.5 rounded bg-surface-sunken text-text-sub whitespace-nowrap">{CATEGORY_LABEL[finding.category] ?? finding.category}</span>
        {/* ⚠️ 2026-09-15本間さんからの報告で修正: amountLabel(「実測差異」等)がtitle属性(hover
            tooltip)にしか入っておらず、金額が何を表すか画面上で常に見えていなかった。可視テキストにする */}
        <span className="text-right">
          <span className="block text-[11px] text-text-sub whitespace-nowrap">{amountLabel}</span>
          <span className="text-sm font-semibold text-text-body tabular-nums">{jpy(finding.impact_amount)}</span>
        </span>
      </div>
      <p className="text-sm text-text-body mt-1 line-clamp-2 break-words">{finding.title}</p>
      <p className="text-xs text-text-sub mt-0.5">{nodeName(finding.node_id)}</p>
      <details className="mt-1">
        <summary className="text-xs text-text-sub cursor-pointer">詳細</summary>
        <p className="text-xs text-text-sub mt-1 whitespace-pre-wrap break-words">{finding.narrative}</p>
      </details>
      {finding.disposition === 'open' ? (
        <div className="flex flex-wrap gap-2 mt-2">
          <Button variant="ghost" size="sm" className="min-h-[44px] w-full sm:w-auto" onClick={onPromote}>施策にする</Button>
          <Button variant="ghost" size="sm" className="min-h-[44px] w-full sm:w-auto" onClick={onDismiss}>却下</Button>
        </div>
      ) : (
        <span className="inline-block mt-2 text-[11px] px-1.5 py-0.5 rounded bg-surface-sunken text-text-sub">
          {finding.disposition === 'promoted' ? '施策化済み' : finding.disposition === 'dismissed' ? '却下済み' : '継続中'}
        </span>
      )}
    </li>
  );
}

function EditMeetingModal({ open, meeting, onClose, onSave, saving }: {
  open: boolean; meeting: BoardMeeting | undefined;
  onClose: () => void;
  onSave: (patch: { title: string; meeting_date: string; target_month: string | null; attendees_note: string | null; status: string }) => void;
  saving: boolean;
}) {
  const [title, setTitle] = useState(meeting?.title ?? '');
  const [date, setDate] = useState(meeting?.meeting_date ?? '');
  const [month, setMonth] = useState(meeting?.target_month?.slice(0, 7) ?? '');
  const [note, setNote] = useState(meeting?.attendees_note ?? '');
  const [status, setStatus] = useState(meeting?.status ?? 'planned');

  // open のたびに現在値へ同期 (簡易実装、Modal は都度アンマウントされない共有インスタンスのため)
  useMemo(() => {
    if (open && meeting) {
      setTitle(meeting.title); setDate(meeting.meeting_date);
      setMonth(meeting.target_month?.slice(0, 7) ?? ''); setNote(meeting.attendees_note ?? ''); setStatus(meeting.status);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Modal open={open} title="会議を編集" onClose={onClose} size="md"
      footer={(
        <>
          <Button variant="secondary" onClick={onClose}>キャンセル</Button>
          <Button variant="primary" loading={saving} disabled={title.trim() === '' || date === ''}
            onClick={() => onSave({ title, meeting_date: date, target_month: month ? `${month}-01` : null, attendees_note: note || null, status })}>
            保存
          </Button>
        </>
      )}>
      <div className="space-y-3">
        <div>
          <label className="block text-xs text-text-sub mb-1" htmlFor="em-title">タイトル</label>
          <input id="em-title" type="text" value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)}
            className="w-full min-h-[44px] px-3 rounded border border-surface-border text-sm" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-text-sub mb-1" htmlFor="em-date">開催日</label>
            <input id="em-date" type="date" value={date} onChange={(e) => setDate(e.target.value)}
              className="w-full min-h-[44px] px-3 rounded border border-surface-border text-sm" />
          </div>
          <div>
            <label className="block text-xs text-text-sub mb-1" htmlFor="em-month">対象月 (任意)</label>
            {/* ネイティブ<input type="month">はSafariで非対応のため生テキスト入力にフォールバックし
                書式検証が効かない(2026-09-15実機で発覚、本間さんから報告)。MonthPickerに置き換える */}
            <MonthPicker id="em-month" aria-label="対象月" value={month} onChange={setMonth}
              className="w-full min-h-[44px] px-3 rounded border border-surface-border text-sm" />
          </div>
        </div>
        <p className="text-xs text-text-sub">
          対象月 = この会議で振り返りたい実績の月（例: 8月の実績を話し合うなら「8月」）。
        </p>
        <div>
          <label className="block text-xs text-text-sub mb-1" htmlFor="em-status">状態</label>
          <Select value={status} onValueChange={(v) => setStatus(v as 'planned' | 'closed')}>
            <SelectTrigger id="em-status" aria-label="状態"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="planned">予定</SelectItem>
              <SelectItem value="closed">終了</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-xs text-text-sub mb-1" htmlFor="em-note">出席メモ (任意)</label>
          <textarea id="em-note" value={note} maxLength={2000} rows={3} onChange={(e) => setNote(e.target.value)}
            className="w-full px-3 py-2 rounded border border-surface-border text-sm" />
        </div>
      </div>
    </Modal>
  );
}
